/**
 * /news/[slug] route — news detail with side-by-side USP layout.
 *
 * Day-1 (founder re-prioritization 2026-05-05):
 *   - Left = NewsContent
 *   - Right = AsklawPanelPlaceholder
 *
 * When citation linker S5-late ships, swap right side → AsklawLegalPanel
 * (Mode A) or ModeBLawPanel (Mode B). Layout component unchanged.
 *
 * SEO:
 *   - meta title + description from news + i18n hotnews
 *   - 3-block JSON-LD via generateNewsArticleJsonLd
 *   - canonical URL https://asklaw.vn/news/[slug]
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '../use-lang';
import { resolveHotNews } from '../i18n/hotnews';
import { fetchNewsDetail, type NewsDetailResponse } from '../lib/news-api';
import { generateNewsArticleJsonLd, renderJsonLdScripts } from '../lib/news-schema-jsonld';
import { NewsDetailLayout } from '../components/news/NewsDetailLayout';
import { NewsContent } from '../components/news/NewsContent';
import { AsklawPanelPlaceholder } from '../components/news/AsklawPanelPlaceholder';
import { NewsAttributionFooter } from '../components/news/NewsAttributionFooter';

export function NewsDetailRoute() {
  const { slug } = useParams();
  const { lang } = useLang();
  const copy = resolveHotNews(lang);
  const [news, setNews] = useState<NewsDetailResponse | null>(null);
  const [error, setError] = useState<'not_found' | 'other' | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setNews(null);
    setError(null);
    fetchNewsDetail(slug)
      .then((data) => {
        if (!cancelled) setNews(data);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message === 'not_found' ? 'not_found' : 'other');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Update <head>: title, description, OG, JSON-LD scripts.
  useEffect(() => {
    if (!news) return;
    document.title = `${news.headline} — Asklaw`;
    const description =
      news.ai_summary?.slice(0, 200) ??
      news.raw_excerpt?.slice(0, 200) ??
      copy.feed_meta_description;
    setMeta('description', description);
    setMeta('og:title', news.headline, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', `https://asklaw.vn/news/${news.slug}`, true);
    setMeta('twitter:card', 'summary_large_image', true);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
    setCanonical(`https://asklaw.vn/news/${news.slug}`);

    const blocks = generateNewsArticleJsonLd(news, lang);
    const serialised = renderJsonLdScripts(blocks);
    return injectJsonLd(serialised);
  }, [news, lang, copy]);

  if (error === 'not_found') {
    return (
      <main style={{ maxWidth: 560, margin: '64px auto', padding: '0 16px', textAlign: 'center' }}>
        <h1 className="h-display" style={{ fontSize: 28 }}>
          {copy.error_detail_404}
        </h1>
        <p style={{ marginTop: 16 }}>
          <Link to="/news" className="cta-primary">
            {lang === 'en' ? 'Back to feed' : 'Quay lại bản tin'}
            <span className="arrow" aria-hidden>{'->'}</span>
          </Link>
        </p>
      </main>
    );
  }

  if (error === 'other') {
    return (
      <main style={{ maxWidth: 720, margin: '32px auto', padding: '0 16px' }}>
        <div role="alert" className="banner banner-warning">
          {copy.error_cache_timeout}
        </div>
      </main>
    );
  }

  if (!news) {
    return (
      <main style={{ maxWidth: 1100, margin: '32px auto', padding: '0 16px' }}>
        <div aria-hidden style={{ height: 280, background: 'var(--asklaw-cream-deep)', borderRadius: 14 }} />
      </main>
    );
  }

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 96px' }}>
      <p style={{ marginBottom: 12 }}>
        <Link
          to="/news"
          style={{ color: 'var(--asklaw-muted)', fontSize: 13, textDecoration: 'none' }}
        >
          {lang === 'en' ? '← Back to feed' : '← Quay lại bản tin'}
        </Link>
      </p>

      <NewsDetailLayout
        left={
          <>
            <NewsContent news={news} />
            <NewsAttributionFooter
              sourceName={news.source_name}
              sourceUrl={news.source_url}
              aiDisclosure={news.ai_disclosure}
            />
          </>
        }
        right={<AsklawPanelPlaceholder newsId={news.id} headline={news.headline} />}
      />
    </main>
  );
}

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(url: string): void {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = url;
}

function injectJsonLd(serialised: string[]): () => void {
  const nodes: HTMLScriptElement[] = [];
  for (const s of serialised) {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = s;
    el.dataset.jsonld = 'asklaw-news';
    document.head.appendChild(el);
    nodes.push(el);
  }
  return () => {
    for (const n of nodes) {
      n.parentNode?.removeChild(n);
    }
  };
}
